import $ from 'jquery';
import _ from 'lodash';
import { getApiAccessToken } from '~auth';
import {
  fetchEditorAttachments,
  displaySidebarAttachments,
} from './sidebarLegacy';

import axios from 'axios';
import axiosRetry from 'axios-retry';
import { getMetadata } from '~loader/loader-utils';
import { isLoggedUserGlobalEditor } from '~src/user';
import editorConfig from '~config/editorConfig';
import { handleSidebarImages } from '~src/sidebar/handleSidebarImages';
import { onErrorShowMessage } from '~src/errors';
import { handleSidebarBackgrounds } from '~src/sidebar/index';
import { getState, modifyState } from '~src/state';
import { unmarshalGraphqlResponse } from '~graphql/utils';
import { handleTabQuotes, cleanHtmlQuote } from './sidebar/quotes';
import {
  createQuote,
  deleteQuote,
  createEditorCanvasPage,
  updateEditorCanvasPage,
  createEditorCanvas,
  updateEditorCanvas,
  fetchEditorCanvasById,
} from './~graphql/controllers';
import { getTextTemplateContext } from './utils';
import { TextTemplate } from '@onoranzefunebricloud/giotto-text-template';
import {
  buildEditorCanvasFormatPointerFields,
  buildEditorCanvasTemplatePointerFields,
  getSelectedTemplateFromCanvas,
} from './utils/editorCanvasPointers';

const getAxiosClient = async (withRetry = false, retries = 3, options = {}) => {
  const token = await getApiAccessToken();
  const auth = `Bearer ${token}`;

  let client = axios.create({
    baseURL: process.env.PARSE_API_URL,
    timeout: 30000,
    headers: {
      Authorization: auth,
    },
  });

  if (withRetry) {
    const retryDelay =
      Number.isFinite(options.retryDelayMs) && options.retryDelayMs >= 0
        ? () => options.retryDelayMs
        : axiosRetry.exponentialDelay;

    axiosRetry(client, {
      retries,
      retryDelay,
      retryCondition: () => true,
      shouldResetTimeout: true,
    });
  }

  return client;
};

const saveEditorPages = async (canvasId, firstSave = false) => {
  if (firstSave === false) {
    const currentPageIndex = getState('currentPageIndex');

    try {
      // salvo la pagina attuale
      // Native Fabric.js serialization already includes properties from additionalJsonProperties
      const canvasJson = window.canvas.toJSON(
        editorConfig.additionalJsonProperties
      );
      const savedPage = JSON.stringify(canvasJson);
      modifyState(`pages[${currentPageIndex}].canvasJson`, savedPage);
    } catch (error) {
      modifyState(`pages[${currentPageIndex}].canvasJson`, '');
    }
  }

  const pagesIds = [];
  let savedPagesCount = 0;

  const format = getState('selected.formato');
  const formatDefault = getState('selected.formatoDefault');

  const pages = getState('pages');

  for (let page of pages) {
    const data = {};

    data.canvasJson = page.canvasJson;
    data.editor_canvas_pointer = {
      link: canvasId,
    };
    data.order = page.order;

    // this IF is an attempt to make thumbnail generation more stable
    if (page.preview && page.preview.url) {
      data.hash = page.hash;
    }

    data.soundtrackId = getState('soundtrackId');
    data.backgroundVideoId = getState('backgroundVideoId');

    if (page.animations) {
      if (typeof page.animations === 'string') {
        data.animations = page.animations;
      } else {
        data.animations = JSON.stringify(page.animations);
      }
    }

    Object.assign(
      data,
      buildEditorCanvasFormatPointerFields(
        { format, formatDefault },
        { clearMissing: Boolean(page.objectId) }
      )
    );

    if (page.objectId) {
      await updateEditorCanvasPage(page.objectId, data);
    } else {
      const obj = await createEditorCanvasPage(data);
      page.objectId = obj.objectId;
    }

    pagesIds.push(page.objectId);
    savedPagesCount++;

    if (savedPagesCount === pages.length) {
      return pagesIds;
    }
  }
};

const createCanvasAndPagesRelation = async (canvasId, pagesIds) => {
  const data = {};

  data.editor_canvas_pages = {
    add: pagesIds,
  };

  await updateEditorCanvas(canvasId, data);
};

const saveEditorCanvas = async (firstSave = false) => {
  const servizio = getState('selected.servizio');
  const projectType = getState('selected.tipo');
  const format = getState('selected.formato');
  const formatDefault = getState('selected.formatoDefault');
  const templatesCategory = getState('selected.templatesCategory');
  const template = getState('selected.template');

  const metadata = getMetadata();

  const data = {
    metadata,
  };

  if (servizio && servizio.objectId) {
    data.defunto_pointer = { link: servizio.objectId };
  }

  if (projectType && projectType.objectId) {
    if (projectType.isGlobal && projectType.isGlobal === true) {
      data.editor_project_types_default_pointer = {
        link: projectType.objectId,
      };
    } else {
      data.editor_project_types_pointer = { link: projectType.objectId };
    }
  }

  Object.assign(
    data,
    buildEditorCanvasFormatPointerFields(
      { format, formatDefault },
      { clearMissing: !firstSave }
    )
  );

  if (templatesCategory && templatesCategory.objectId) {
    if (templatesCategory.isGlobal && templatesCategory.isGlobal === true) {
      data.editor_templates_categories_default_pointer = {
        link: templatesCategory.objectId,
      };
    } else {
      data.editor_templates_categories_pointer = {
        link: templatesCategory.objectId,
      };
    }
  }

  Object.assign(
    data,
    buildEditorCanvasTemplatePointerFields(template, {
      clearOpposite: !firstSave,
    })
  );

  let canvasId;
  let obj;

  if (firstSave) {
    obj = await createEditorCanvas(data);
    canvasId = obj.objectId;
  } else {
    canvasId = window.editorLoader.canvasId;
    await updateEditorCanvas(canvasId, data);
  }

  // I have saved the canvas, now I will save all the pages
  const pagesIds = await saveEditorPages(canvasId, firstSave);
  // Now that I have saved each page and the canvas, I create a relation from
  // the canvas and the pages
  await createCanvasAndPagesRelation(canvasId, pagesIds);

  return canvasId;
};

const saveServizio = async (payload) => {
  const httpClient = await getAxiosClient();

  const url = `${process.env.PARSE_API_URL}/defunto`;
  const method = 'POST';

  return httpClient({ url, method, data: payload, responseType: 'json' });
};

const saveQuote = async (testo, category) => {
  const fields = {
    testo,
    category,
  };

  const {
    servizio,
    user,
    deleganti,
    necrofori,
    card,
    serviceReservation,
  } = await getTextTemplateContext();

  const textTemplate = new TextTemplate(
    servizio,
    user,
    deleganti,
    necrofori,
    card,
    serviceReservation
  );

  try {
    textTemplate.renderMustache(testo);
  } catch (error) {
    console.error(error);
    onErrorShowMessage(false, {
      icon: 'error',
      title: 'Attenzione!',
      confirmButtonColor: '#278e8e',
      text:
        'Impossibile salvare il testo. Controlla di aver scritto il testo correttamente.',
    });
    $('#button-save-quote').prop('disabled', false);
    $('#button-clean-html-quotes').prop('disabled', false);
    $('#button-clean-html-quotes').show();
    $('#editor-add-quote-value').prop('disabled', false);
    $('#editor-btn-spinner-quotes').hide();
    $('#button-save-quote span').text('Salva');
    return;
  }

  try {
    await createQuote(fields);
    handleTabQuotes(true, category);
    cleanHtmlQuote();
  } catch (error) {
    console.error(error);
    onErrorShowMessage();
  }
};

const removeQuote = async (id) => {
  await deleteQuote(id);

  try {
    handleTabQuotes(true);
    cleanHtmlQuote();
  } catch (error) {
    console.error(error);
    onErrorShowMessage();
  }
};

const deleteImages = async (id, isGlobal) => {
  const httpClient = await getAxiosClient();
  let tableName;

  if (typeof isGlobal === 'boolean') {
    tableName = isGlobal ? 'editor_images_default' : 'editor_images';
  } else {
    const isGlobalEditor = await isLoggedUserGlobalEditor();
    tableName = isGlobalEditor ? 'editor_images_default' : 'editor_images';
  }

  const url = `${process.env.PARSE_API_URL}/${tableName}/${id}`;
  const method = 'DELETE';
  try {
    await httpClient({ url, method, responseType: 'json' });
    await handleSidebarImages(true);
  } catch (error) {
    console.error(error);
    throw error;
  }
};

const deletePointerAttachmentFromDefunto = async () => {
  const httpClient = await getAxiosClient();

  const servizio = getState('selected.servizio');
  const url = `${process.env.PARSE_API_URL}/defunto/${servizio.objectId}`;
  const method = 'PUT';
  const data = {
    unset_attributes: [{ column: 'allegati_pointer' }],
  };
  try {
    await httpClient({ url, method, data: { data }, responseType: 'json' });
  } catch (error) {
    console.error(error);
    onErrorShowMessage();
    throw error;
  }
};

const deleteAttachment = async (idAttachment) => {
  const httpClient = await getAxiosClient();

  const url = `${process.env.PARSE_API_URL}/allegati/${idAttachment}`;
  const method = 'DELETE';
  try {
    await httpClient({ url, method, responseType: 'json' });
    const { attachments } = await fetchEditorAttachments(true);
    window.editorConfig.sidebarAttachments = attachments;
    displaySidebarAttachments();
  } catch (error) {
    console.error(error);
    onErrorShowMessage();
    throw error;
  }
};

const setAttachmentAsProfilePhoto = async (idAttachment, idDefunto) => {
  const httpClient = await getAxiosClient();

  const url = `${process.env.PARSE_API_URL}/defunto/${idDefunto}`;
  const method = 'PUT';
  const data = {
    allegati_pointer: idAttachment,
  };
  try {
    await httpClient({ url, method, data: { data }, responseType: 'json' });

    const { attachments } = await fetchEditorAttachments(true);
    window.editorConfig.sidebarAttachments = attachments;

    const newProfilePhoto = attachments.items.find(
      (attachment) => attachment.objectId === idAttachment
    );
    modifyState('selected.servizio.allegati_pointer', newProfilePhoto);
    displaySidebarAttachments();
  } catch (error) {
    console.error(error);
    onErrorShowMessage();
  }
};

const deleteBackgrounds = async (id) => {
  const httpClient = await getAxiosClient();
  let tableName;
  const isGlobalEditor = await isLoggedUserGlobalEditor();

  if (isGlobalEditor) {
    tableName = 'editor_backgrounds_default';
  } else {
    tableName = 'editor_backgrounds';
  }

  const url = `${process.env.PARSE_API_URL}/${tableName}/${id}`;
  const method = 'DELETE';

  try {
    await httpClient({ url, method, responseType: 'json' });
    await handleSidebarBackgrounds(true);
  } catch (error) {
    console.error(error);
    onErrorShowMessage();
  }
};

const fetchCanvas = async (id) => {
  const obj = await fetchEditorCanvasById(id);

  if (!obj.objectId) {
    const error = new Error('Canvas not found');
    return error;
  }

  // required to remove "edges" from the relation
  if (_.has(obj, 'defunto_pointer.relation_necrofori')) {
    _.set(
      obj,
      'defunto_pointer.relation_necrofori',
      unmarshalGraphqlResponse(obj.defunto_pointer, 'relation_necrofori', true)
    );
  }

  const sortedPages = obj.editor_canvas_pages.sort((a, b) => a.order - b.order);

  const parseAnimationsInPages = sortedPages.map((page) => {
    if (page.animations) {
      page.animations = JSON.parse(page.animations);
    }
    if (page.soundtrackId) {
      modifyState('soundtrackId', page.soundtrackId);
    }
    if (page.backgroundVideoId) {
      modifyState('backgroundVideoId', page.backgroundVideoId);
    }
    return page;
  });

  modifyState('pages', parseAnimationsInPages);

  // Servizio
  const servizio = obj.defunto_pointer;
  if (servizio) {
    modifyState('selected.servizio', servizio);
  }

  // Tipo - Default
  if (obj.editor_project_types_default_pointer) {
    modifyState('selected.tipo', obj.editor_project_types_default_pointer);
  }

  // Tipo - Custom
  if (obj.editor_project_types_pointer) {
    modifyState('selected.tipo', obj.editor_project_types_pointer);
  }

  // Formato - Custom
  if (obj.editor_project_formats_pointer) {
    modifyState('selected.formato', obj.editor_project_formats_pointer);
  } else {
    modifyState('selected.formato', {});
  }

  // Formato - Default
  if (obj.editor_project_formats_default_pointer) {
    modifyState(
      'selected.formatoDefault',
      obj.editor_project_formats_default_pointer
    );
  } else {
    modifyState('selected.formatoDefault', undefined);
  }

  const selectedTemplate = getSelectedTemplateFromCanvas(obj);
  if (selectedTemplate) {
    modifyState('selected.template', selectedTemplate);
  }

  // Template Category default
  if (obj.editor_templates_categories_default_pointer) {
    modifyState(
      'selected.templatesCategory',
      obj.editor_templates_categories_default_pointer
    );
  }

  // Template Category
  if (obj.editor_templates_categories_pointer) {
    modifyState(
      'selected.templatesCategory',
      obj.editor_templates_categories_pointer
    );
  }

  return obj;
};

export {
  getAxiosClient,
  saveEditorCanvas,
  fetchCanvas,
  saveQuote,
  saveServizio,
  removeQuote,
  deleteImages,
  deleteBackgrounds,
  deleteAttachment,
  deletePointerAttachmentFromDefunto,
  setAttachmentAsProfilePhoto,
};
